import React, { createContext, useCallback, useContext, useState } from "react";
import type { ShellNotification } from "../types";

interface NotificationContextValue {
  notifications: ShellNotification[];
  pushNotification: (n: Omit<ShellNotification, "id" | "timestamp">) => void;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<ShellNotification[]>([]);

  const pushNotification = useCallback((n: Omit<ShellNotification, "id" | "timestamp">) => {
    setNotifications((prev) => {
      // Deduplicate by source + title so polling doesn't create duplicates
      if (prev.some((x) => x.source === n.source && x.title === n.title)) return prev;
      return [
        ...prev,
        { ...n, id: `${n.source}-${n.title}`, timestamp: Date.now() },
      ];
    });
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id || n.persistent));
  }, []);

  const clearAll = useCallback(() => setNotifications([]), []);

  return (
    <NotificationContext.Provider value={{ notifications, pushNotification, dismissNotification, clearAll }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
